import { splitH3Entries } from "../parse-sections";
import type {
  Actor,
  ApiEndpoint,
  ExternalSystem,
  ExternalSystemKind,
  Permission,
  System,
} from "../types";
import { parseAssumptions } from "../v3/parse-assumptions-v3";
import { asString, parseBoldEntriesV3 } from "../v3/parse-bullets-v3";
import { EXTERNAL_KIND_VALUES, parseActors, parseCluster, parseSystem } from "../v3/parse-l1-v3";
import { parseRisks } from "../v3/parse-risks-v3";
import { parseEndpoints } from "./parse-endpoints-v4";
import { parsePermissions } from "./parse-permissions-v4";

// archviz/v0.4 L1 parser.
//
// Identical to v0.3 except External Systems entries may carry optional
// `permissions:` and `endpoints:` blocks for L3-of-external API surface
// detail. System and Actors parsing reuses v3 directly.
//
// Cross-reference policy between permissions[].endpoints[] (forward) and
// endpoint.permission (back): the endpoint's `permission` field wins.
//   - Missing back-ref (perm lists endpoint X, X.permission unset) → silently
//     backfill X.permission from the forward ref. Common author-time
//     mistake; not worth a warning.
//   - Conflict (X.permission = A, perm B forward-refs X) → keep X.permission,
//     warn. The endpoint is the more specific entity.
//   - Forward ref to unknown endpoint → warn, drop the forward entry.
//   - Back ref to unknown permission → warn, leave the value alone so the
//     renderer can surface "missing perm".

interface L1Result {
  system?: System;
  actors: Actor[];
  externalSystems: ExternalSystem[];
}

export function parseL1V4(body: string): L1Result {
  const result: L1Result = { actors: [], externalSystems: [] };
  const subs = splitH3Entries(body);
  for (const sub of subs) {
    const title = sub.title.toLowerCase().trim();
    if (title === "system") {
      result.system = parseSystem(sub.body);
    } else if (title === "actors") {
      result.actors = parseActors(sub.body);
    } else if (title === "external systems") {
      result.externalSystems = parseExternalsV4(sub.body);
    }
    // Other H3 titles ignored silently — section is allowed to grow.
  }
  return result;
}

function parseExternalsV4(body: string): ExternalSystem[] {
  const entries = parseBoldEntriesV3(body, "L1.ExternalSystems");
  return entries.map((entry) => {
    const p = entry.properties;
    const scope = `L1.ExternalSystems.${entry.id}`;
    const rawKind = (asString(p.kind) ?? "").toLowerCase();
    const kind: ExternalSystemKind | undefined =
      rawKind === ""
        ? undefined
        : EXTERNAL_KIND_VALUES.has(rawKind as ExternalSystemKind)
          ? (rawKind as ExternalSystemKind)
          : "other";
    const risks = parseRisks(p.risks, { scope, strict: true });
    const assumptions = parseAssumptions(p.assumptions, { scope, strict: true });
    const permissions = parsePermissions(p.permissions, { scope, strict: true });
    const endpoints = parseEndpoints(p.endpoints, { scope, strict: true });
    const reconciled = reconcileCrossRefs(permissions, endpoints, scope);
    return {
      id: entry.id,
      label: asString(p.label) ?? entry.id,
      headline: asString(p.headline),
      description: asString(p.description),
      why: asString(p.why),
      kind,
      url: asString(p.url),
      cluster: parseCluster(p.cluster, scope),
      risks,
      assumptions,
      permissions: reconciled.permissions.length > 0 ? reconciled.permissions : undefined,
      endpoints: reconciled.endpoints.length > 0 ? reconciled.endpoints : undefined,
      // Mirror canonical risks into the deprecated v0.2 `risk` string array
      // so the existing renderer keeps working until Phase 5 migrates it.
      risk: risks.map((r) => r.description),
    };
  });
}

// Reconcile the bidirectional permission↔endpoint references per the
// policy in the file header. Mutates the input arrays in place AND returns
// them for call-site clarity.
function reconcileCrossRefs(
  permissions: Permission[],
  endpoints: ApiEndpoint[],
  scope: string,
): { permissions: Permission[]; endpoints: ApiEndpoint[] } {
  const permByScope = new Map(permissions.map((p) => [p.scope, p]));
  const endpointById = new Map(endpoints.map((e) => [e.id, e]));

  for (const perm of permissions) {
    if (!perm.endpoints) continue;
    const kept: string[] = [];
    for (const endpointId of perm.endpoints) {
      const endpoint = endpointById.get(endpointId);
      if (!endpoint) {
        console.warn(
          `[arch-parser v0.4] ${scope}: permission "${perm.scope}" lists unknown endpoint "${endpointId}" — dropping forward ref.`,
        );
        continue;
      }
      kept.push(endpointId);
      if (!endpoint.permission) {
        endpoint.permission = perm.scope;
      } else if (endpoint.permission !== perm.scope) {
        console.warn(
          `[arch-parser v0.4] ${scope}: cross-reference conflict — endpoint "${endpointId}".permission="${endpoint.permission}" but permission "${perm.scope}".endpoints[] lists it. Keeping endpoint.permission ("${endpoint.permission}") as authoritative.`,
        );
      }
    }
    perm.endpoints = kept.length > 0 ? kept : undefined;
  }

  for (const endpoint of endpoints) {
    if (endpoint.permission && !permByScope.has(endpoint.permission)) {
      console.warn(
        `[arch-parser v0.4] ${scope}: endpoint "${endpoint.id}".permission="${endpoint.permission}" references unknown permission. The renderer will surface this as a missing scope.`,
      );
    }
  }

  return { permissions, endpoints };
}
